// SubscriptionModal Component - Plan Selection
// Phase 4: Stripe Integration
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './SubscriptionModal.css';

const API_BASE = '/api';

const SubscriptionModal = ({ isOpen, onClose, onSuccess, highlightTier = null }) => {
  const { user, token, refreshSubscription } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTiers();
      setError('');
      // Refresh subscription status when modal opens
      if (refreshSubscription) {
        refreshSubscription();
      }
    }
  }, [isOpen]);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API_BASE}/subscription/tiers`);
      const data = await response.json();
      setTiers(data.tiers);
      setLoading(false);
    } catch (err) {
      setError('Failed to load subscription plans');
      setLoading(false);
    }
  };

  const handleSelectTier = async (tier) => {
    if (tier.id === 'pilgrim') {
      // Downgrade to free - send to portal to cancel
      handleManageSubscription();
      return;
    }

    if (tier.comingSoon) {
      return; // Can't select coming soon tiers
    }

    // Check if this is already the user's tier
    const currentTierValue = user?.subscriptionTier || 'pilgrim';
    if (tier.id === currentTierValue) {
      return;
    }

    // If user already has a paid subscription, send them to portal to change plans
    if (currentTierValue !== 'pilgrim') {
      handleManageSubscription();
      return;
    }

    // New subscription - create checkout session
    setProcessingTier(tier.id);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tier: tier.id })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;

    } catch (err) {
      setError(err.message);
      setProcessingTier(null);
    }
  };

  const handleManageSubscription = async () => {
    setProcessingTier('manage');
    setError('');

    try {
      const response = await fetch(`${API_BASE}/subscription/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to open subscription portal');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;

    } catch (err) {
      setError(err.message);
      setProcessingTier(null);
    }
  };

  if (!isOpen) return null;

  const currentTier = user?.subscriptionTier || 'pilgrim';
  const hasActiveSubscription = user?.subscriptionStatus === 'active' || 
                                 user?.subscriptionStatus === 'premium' ||
                                 (user?.subscriptionTier && user?.subscriptionTier !== 'pilgrim');

  // Tier hierarchy for comparison
  const tierRank = {
    pilgrim: 0,
    disciple: 1,
    companion: 2,
    retreatant: 3
  };

  const getButtonText = (tierId, currentTierId) => {
    const tierRankValue = tierRank[tierId] || 0;
    const currentRankValue = tierRank[currentTierId] || 0;

    if (currentTierId === 'pilgrim' || currentRankValue === 0) {
      // User is on free plan
      return tierId === 'pilgrim' ? 'Current Plan' : 'Start Free Trial';
    }

    if (tierRankValue > currentRankValue) {
      return 'Upgrade';
    } else if (tierRankValue < currentRankValue) {
      return tierId === 'pilgrim' ? 'Downgrade to Free' : 'Downgrade';
    }
    
    return 'Select';
  };

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div className="subscription-modal" onClick={(e) => e.stopPropagation()}>
        <button className="subscription-modal-close" onClick={onClose}>×</button>
        
        <h2 className="subscription-modal-title">Choose Your Path</h2>
        <p className="subscription-modal-subtitle">
          Select a plan that fits your spiritual journey
        </p>

        {error && (
          <div className="subscription-error">{error}</div>
        )}

        {loading ? (
          <div className="subscription-loading">Loading plans...</div>
        ) : (
          <div className="subscription-tiers">
            {tiers.map((tier) => {
              const isCurrentTier = tier.id === currentTier;
              const isHighlighted = tier.id === highlightTier;
              const isPopular = tier.popular;
              const isComingSoon = tier.comingSoon;
              const isProcessing = processingTier === tier.id;

              return (
                <div 
                  key={tier.id}
                  className={`subscription-tier ${isCurrentTier ? 'current' : ''} ${isHighlighted ? 'highlighted' : ''} ${isPopular ? 'popular' : ''} ${isComingSoon ? 'coming-soon' : ''}`}
                >
                  {isPopular && <span className="tier-badge popular">Most Popular</span>}
                  {isComingSoon && <span className="tier-badge coming-soon">Coming Soon</span>}
                  
                  <h3 className="tier-name">{tier.name}</h3>
                  <div className="tier-price">{tier.priceDisplay}</div>
                  
                  <ul className="tier-features">
                    {tier.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="feature-check">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    className={`tier-button ${isCurrentTier ? 'current' : ''} ${isComingSoon ? 'disabled' : ''}`}
                    onClick={() => handleSelectTier(tier)}
                    disabled={isCurrentTier || isComingSoon || isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 
                     isCurrentTier ? 'Current Plan' : 
                     isComingSoon ? 'Coming Soon' :
                     getButtonText(tier.id, currentTier)}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {hasActiveSubscription && (
          <div className="subscription-manage">
            <button 
              className="manage-subscription-btn"
              onClick={handleManageSubscription}
              disabled={processingTier === 'manage'}
            >
              {processingTier === 'manage' ? 'Opening...' : 'Manage Subscription'}
            </button>
          </div>
        )}

        <p className="subscription-note">
          All plans include a 14-day free trial. Cancel anytime.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionModal;
